import { useEffect, useRef } from 'react'
import { fetchWithAuth, isOfflineOrUnreachableFetchError } from '@/lib/api'

const POLL_MS = 4500
const MAX_BACKOFF_MS = 60_000

function nextBackoffMs(current: number, failed: boolean): number {
  if (!failed) return POLL_MS
  return Math.min(MAX_BACKOFF_MS, Math.max(15_000, current * 2))
}

/**
 * Polls GET /api/content-revision and dispatches:
 * - `flownotes-content-revision` when content revision increases (body edits)
 * - `flownotes-structure-revision` when structureRevision increases (path/title/folder ops)
 * Backs off on 5xx (except 429) and 401/403 so a down DB or expired session
 * does not hammer the API every few seconds.
 */
export function useContentRevisionPoll() {
  const lastRevisionRef = useRef<number | null>(null)
  const lastStructureRevisionRef = useRef<number | null>(null)
  const backoffMsRef = useRef(POLL_MS)

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const schedule = (delay: number) => {
      if (cancelled) return
      timeoutId = setTimeout(() => void tick(), delay)
    }

    const tick = async () => {
      if (cancelled) return
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        schedule(backoffMsRef.current)
        return
      }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        schedule(backoffMsRef.current)
        return
      }
      let failed = false
      try {
        const res = await fetchWithAuth('/api/content-revision')
        if (cancelled) return
        if (!res.ok) {
          failed =
            (res.status >= 500 && res.status !== 429) ||
            res.status === 401 ||
            res.status === 403
          backoffMsRef.current = nextBackoffMs(backoffMsRef.current, failed)
          schedule(backoffMsRef.current)
          return
        }
        backoffMsRef.current = POLL_MS
        const data = (await res.json()) as {
          success?: boolean
          revision?: number
          structureRevision?: number
        }
        if (data.revision === undefined || typeof data.revision !== 'number') {
          schedule(backoffMsRef.current)
          return
        }

        const prevContent = lastRevisionRef.current
        const prevStructure = lastStructureRevisionRef.current
        lastRevisionRef.current = data.revision
        if (typeof data.structureRevision === 'number') {
          lastStructureRevisionRef.current = data.structureRevision
        }

        if (prevContent !== null && data.revision > prevContent) {
          window.dispatchEvent(
            new CustomEvent('flownotes-content-revision', {
              detail: { revision: data.revision },
            })
          )
        }

        if (
          typeof data.structureRevision === 'number' &&
          prevStructure !== null &&
          data.structureRevision > prevStructure
        ) {
          window.dispatchEvent(
            new CustomEvent('flownotes-structure-revision', {
              detail: { structureRevision: data.structureRevision },
            })
          )
        }
      } catch (err) {
        failed = true
        backoffMsRef.current = nextBackoffMs(backoffMsRef.current, failed)
        if (!isOfflineOrUnreachableFetchError(err)) {
          console.warn('[content-revision] poll failed:', err)
        }
      }
      schedule(backoffMsRef.current)
    }

    void tick()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (timeoutId) clearTimeout(timeoutId)
        void tick()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    const onOnline = () => {
      if (cancelled) return
      backoffMsRef.current = POLL_MS
      if (timeoutId) clearTimeout(timeoutId)
      void tick()
    }
    window.addEventListener('online', onOnline)

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('online', onOnline)
    }
  }, [])
}
